/**
 * Кадр телефона для Sky Race: `{type: '…SkyRaceData', gameState, level?, mode?, detectionProgress?, pose?}`.
 * Поза — четыре точки управления (плечи и запястья), 0…1, y вниз, в зеркальном кадре камеры; без любой из них
 * `pose` — `null` (кадр без позы — смена состояния или игрока нет в кадре). `level` и `mode` телефон шлёт в каждом
 * кадре: страница на ТВ собирается под них до старта и заново после рестарта.
 */
export type SkyRaceGameState = 'PREPARING' | 'STARTED' | 'PAUSED' | 'GAME_OVER';

export type SkyRaceMode = 'timed' | 'standard';

export const SKY_RACE_JOINTS = ['leftShoulder', 'rightShoulder', 'leftWrist', 'rightWrist'] as const;

export type SkyRacePose = Record<(typeof SKY_RACE_JOINTS)[number], { x: number; y: number }>;

export interface SkyRaceFrame {
  gameState: SkyRaceGameState;
  /** `null` — в кадре нет `level` (или он не строка): уровень не менять. */
  level: string | null;
  /** `null` — в кадре нет `mode` (или он неизвестен): режим не менять. */
  mode: SkyRaceMode | null;
  /** Заполнение рамки калибровки на телефоне, 0…1; `null` — поля нет. */
  detectionProgress: number | null;
  pose: SkyRacePose | null;
}

const STATES: readonly unknown[] = ['PREPARING', 'STARTED', 'PAUSED', 'GAME_OVER'];
const MODES: readonly unknown[] = ['timed', 'standard'];

function parsePoint(value: unknown): { x: number; y: number } | null {
  if (!value || typeof value !== 'object') return null;
  const { x, y } = value as Record<string, unknown>;
  return typeof x === 'number' && typeof y === 'number' && Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

function parsePose(value: unknown): SkyRacePose | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const joints = SKY_RACE_JOINTS.map((joint) => [joint, parsePoint(raw[joint])] as const);
  if (joints.some(([, point]) => point === null)) return null;
  return Object.fromEntries(joints) as SkyRacePose;
}

export function parseSkyRaceFrame(raw: unknown): SkyRaceFrame | null {
  if (!raw || typeof raw !== 'object') return null;
  const {
    gameState, level, mode, detectionProgress, pose,
  } = raw as Record<string, unknown>;
  if (!STATES.includes(gameState)) return null;
  return {
    gameState: gameState as SkyRaceGameState,
    level: typeof level === 'string' && level ? level : null,
    mode: MODES.includes(mode) ? (mode as SkyRaceMode) : null,
    detectionProgress:
      typeof detectionProgress === 'number' && Number.isFinite(detectionProgress)
        ? Math.min(1, Math.max(0, detectionProgress))
        : null,
    pose: parsePose(pose),
  };
}
